import React, {
  FC,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAgent, AgentMode } from '../../../contexts/AgentContext';
import { Agent } from '@dreamai/types';
import { useCompany } from '../../../contexts/CompanyContext';
import { ReactComponent as SettingsIcon } from '../../../icons/nav-links/settings.svg';
import { ReactComponent as LogoutIcon } from '../../../icons/nav-links/logout.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MemoryIcon } from '../../../icons/nav-links/memory.svg';
import { ReactComponent as SalesInboxIcon } from '../../../icons/nav-links/sales-inbox.svg';
import { ReactComponent as CampaignsIcon } from '../../../icons/campaigns.svg';
import { ReactComponent as TodoClipboardIcon } from '../../../icons/todo-clipboard.svg';
import { ReactComponent as TodayIcon } from '../../../icons/today.svg';
import { ReactComponent as DreamIcon } from '../../../icons/dream-icon.svg';
import { ReactComponent as ChatHistoryIcon } from '../../../icons/chat-history.svg';
import { ReactComponent as ProjectsFolderIcon } from '../../../icons/projects-folder.svg';
import { useSalesInboxCount } from '../SalesInbox/useSalesInboxCount';
import { useTasksCount } from './useTasksCount';
import { useProjectsUnreadCount } from './useProjectsUnreadCount';
import { useUnconfirmedMemoryCount } from './useUnconfirmedMemoryCount';
import TodoListBadge from './TodoListBadge';
import RecentsDrawer from './RecentsDrawer';
import ProjectsDrawer from './ProjectsDrawer';
import CampaignsDrawer from './CampaignsDrawer';
import './expansive-side-navigation.scss';

const AGENT_TYPE_TITLES: Record<string, string> = {
  'Personal Assistant': 'Personal assistant',
  'Sales Assistant': 'Business assistant',
  'System Assistant': 'System assistant',
};

const AGENT_TYPE_TO_MODE: Record<string, AgentMode> = {
  'Personal Assistant': 'personal',
  'Sales Assistant': 'business',
  'System Assistant': 'system',
};

const SWITCHABLE_TYPES = [
  'Personal Assistant',
  'Sales Assistant',
  'System Assistant',
];

const ExpansiveSideNavigation: FC = () => {
  const {
    agents,
    selectedAgent,
    setSelectedAgent,
    loadAgents,
    agentMode,
    setAgentMode,
    activeAgent,
  } = useAgent();
  const { logout } = useAuth0();
  const { memoryEnabled, isOpsInternalUser } = useCompany();
  const location = useLocation();
  const { needsAttentionCount: salesInboxUnreadCount } = useSalesInboxCount();
  const { pendingCount: tasksCount, urgentCount: urgentTasksCount } =
    useTasksCount(isOpsInternalUser);
  const { unconfirmedCount: unconfirmedMemoryCount } =
    useUnconfirmedMemoryCount();
  const { unreadCount: projectsUnreadCount } = useProjectsUnreadCount(
    activeAgent?.id,
  );

  // Derive which agent types are available
  const hasPA = useMemo(
    () => agents.some((a) => (a.agent_type as string) === 'Personal Assistant'),
    [agents],
  );
  const hasSA = useMemo(
    () => agents.some((a) => (a.agent_type as string) === 'Sales Assistant'),
    [agents],
  );

  // Assistant switcher state
  const [showSwitcherCard, setShowSwitcherCard] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // ── Recents drawer state ────────────────────────────────────────────────
  // The "Recents" nav link toggles a side-drawer that slides out next to
  // the nav and lists the user's recent QuickChat threads. The toggle
  // ref is forwarded into the drawer so its click-outside detection
  // ignores the toggle button (otherwise toggling would immediately
  // close-then-reopen).
  const [recentsOpen, setRecentsOpen] = useState(false);
  const recentsToggleRef = useRef<HTMLButtonElement>(null);

  // ── Projects drawer state ─────────────────────────────────────────────
  const [projectsOpen, setProjectsOpen] = useState(false);
  const projectsToggleRef = useRef<HTMLButtonElement>(null);

  // ── Campaigns drawer state ────────────────────────────────────────────
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const campaignsToggleRef = useRef<HTMLButtonElement>(null);

  const switchableAgents = useMemo(
    () =>
      SWITCHABLE_TYPES.map((type) =>
        agents.find((a) => (a.agent_type as string) === type),
      ).filter((a): a is Agent => a != null),
    [agents],
  );

  const showAssistantSwitcher = switchableAgents.length > 1;

  // Close switcher on click outside
  useEffect(() => {
    if (!showSwitcherCard) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        switcherRef.current &&
        !switcherRef.current.contains(e.target as Node)
      ) {
        setShowSwitcherCard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSwitcherCard]);

  const handleSwitchAgent = useCallback(
    (agent: Agent) => {
      const mode = AGENT_TYPE_TO_MODE[agent.agent_type as string];
      if (mode) setAgentMode(mode);
      setShowSwitcherCard(false);
    },
    [setAgentMode],
  );

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Auto-select default agent (kept for downstream consumers of selectedAgent)
  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      const defaultAgent =
        agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ??
        agents[0];
      setSelectedAgent({
        value: defaultAgent.id,
        label:
          defaultAgent.nickname ?? defaultAgent.name ?? defaultAgent.agent_type,
        dreamieImageUrl: defaultAgent.image_url,
        predominantColour: defaultAgent.predominant_colour,
      });
    }
  }, [agents, selectedAgent, setSelectedAgent]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isActive = (path: string, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <nav
      className={`expansive-side-navigation${recentsOpen ? ' recents-open' : ''}${projectsOpen ? ' projects-open' : ''}${campaignsOpen ? ' campaigns-open' : ''}`}
    >
      <div className='header'>
        <div className='logo'>
          <Link to='/'>
            <DreamIcon className='logo-icon' />
          </Link>
        </div>
      </div>

      <div className='main-links'>
        {isOpsInternalUser ? (
          <Link
            to='/tasks'
            className={`nav-link ${isActive('/tasks') ? 'active' : ''}`}
            data-tooltip='Tasks'
          >
            <ClipboardIcon />
            <span className='nav-label'>Tasks</span>
            {urgentTasksCount > 0 ? (
              <span className='tasks-badge urgent'>{urgentTasksCount}</span>
            ) : tasksCount > 0 ? (
              <span className='tasks-badge'>{tasksCount}</span>
            ) : null}
          </Link>
        ) : (
          <>
            {(hasPA || hasSA) && (
              <Link
                to='/'
                className={`nav-link ${isActive('/', true) ? 'active' : ''}`}
                data-tooltip='Home'
              >
                <TodayIcon />
                <span className='nav-label'>Home</span>
              </Link>
            )}
            {(hasPA || hasSA) && (
              <button
                ref={recentsToggleRef}
                type='button'
                className={`nav-link nav-link-recents ${recentsOpen ? 'active' : ''}`}
                data-tooltip='Chats'
                onClick={() => {
                  setProjectsOpen(false);
                  setCampaignsOpen(false);
                  setRecentsOpen((v) => !v);
                }}
                aria-expanded={recentsOpen}
                aria-haspopup='dialog'
              >
                <ChatHistoryIcon />
                <span className='nav-label'>Chats</span>
              </button>
            )}
            {(hasPA || hasSA) && agentMode !== 'business' && (
              <button
                ref={projectsToggleRef}
                type='button'
                className={`nav-link nav-link-projects ${projectsOpen ? 'active' : ''}`}
                data-tooltip='Projects'
                onClick={() => {
                  setRecentsOpen(false);
                  setCampaignsOpen(false);
                  setProjectsOpen((v) => !v);
                }}
                aria-expanded={projectsOpen}
                aria-haspopup='dialog'
              >
                <ProjectsFolderIcon />
                <span className='nav-label'>Projects</span>
                {projectsUnreadCount > 0 && (
                  <span className='unread-badge'>{projectsUnreadCount}</span>
                )}
              </button>
            )}
            {hasSA && agentMode === 'business' && (
              <button
                ref={campaignsToggleRef}
                type='button'
                className={`nav-link nav-link-campaigns ${campaignsOpen ? 'active' : ''}`}
                data-tooltip='Campaigns'
                onClick={() => {
                  setRecentsOpen(false);
                  setProjectsOpen(false);
                  setCampaignsOpen((v) => !v);
                }}
                aria-expanded={campaignsOpen}
                aria-haspopup='dialog'
              >
                <CampaignsIcon />
                <span className='nav-label'>Campaigns</span>
              </button>
            )}
            {(hasPA || hasSA) && (
              <Link
                to='/todos'
                className={`nav-link ${isActive('/todos') ? 'active' : ''}`}
                data-tooltip='Task Manager'
              >
                <TodoClipboardIcon />
                <span className='nav-label'>Task Manager</span>
                <TodoListBadge />
              </Link>
            )}
            {memoryEnabled && (
              <Link
                to='/memory'
                className={`nav-link ${isActive('/memory') ? 'active' : ''}`}
                data-tooltip='Memories'
              >
                <MemoryIcon />
                <span className='nav-label'>Memories</span>
                {unconfirmedMemoryCount > 0 && <span className='unread-dot' />}
              </Link>
            )}
            {hasSA && agentMode === 'business' && (
              <Link
                to='/sales-inbox'
                className={`nav-link ${isActive('/sales-inbox') ? 'active' : ''}`}
                data-tooltip='Sales Inbox'
              >
                <SalesInboxIcon />
                <span className='nav-label'>Sales Inbox</span>
                {salesInboxUnreadCount > 0 && (
                  <span className='unread-badge'>{salesInboxUnreadCount}</span>
                )}
              </Link>
            )}
          </>
        )}
      </div>

      <div className='footer'>
        {!isOpsInternalUser && showAssistantSwitcher && (
          <div className='assistant-switcher' ref={switcherRef}>
            <button
              className='switcher-avatar-btn'
              onClick={() => setShowSwitcherCard((prev) => !prev)}
              title='Switch assistant'
            >
              {activeAgent?.image_url && (
                <img
                  src={activeAgent.image_url}
                  alt={activeAgent.nickname || activeAgent.name || ''}
                />
              )}
            </button>

            {showSwitcherCard && (
              <div className='switcher-card'>
                <div className='switcher-header'>Switch assistant</div>
                {switchableAgents.map((agent) => (
                  <button
                    key={agent.id}
                    className={`switcher-agent${
                      activeAgent?.id === agent.id ? ' active' : ''
                    }`}
                    onClick={() => handleSwitchAgent(agent)}
                  >
                    <div className='switcher-agent-avatar'>
                      {agent.image_url && <img src={agent.image_url} alt='' />}
                    </div>
                    <div className='switcher-agent-info'>
                      <div className='switcher-agent-nickname'>
                        {agent.nickname || agent.name || agent.agent_type}
                      </div>
                      <div className='switcher-agent-title'>
                        {AGENT_TYPE_TITLES[agent.agent_type as string] ||
                          agent.agent_type}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <Link to='/settings' data-tooltip='Settings'>
          <SettingsIcon />
          <span className='nav-label'>Settings</span>
        </Link>
        <button onClick={handleLogout} data-tooltip='Log out'>
          <LogoutIcon />
          <span className='nav-label'>Log out</span>
        </button>
      </div>

      <RecentsDrawer
        open={recentsOpen}
        agentId={activeAgent?.id}
        onClose={() => setRecentsOpen(false)}
        toggleRef={recentsToggleRef}
      />
      <ProjectsDrawer
        open={projectsOpen}
        agentId={activeAgent?.id}
        agentName={activeAgent?.nickname || activeAgent?.name}
        agentImageUrl={activeAgent?.image_url}
        agentType={activeAgent?.agent_type as string}
        onClose={() => setProjectsOpen(false)}
        toggleRef={projectsToggleRef}
      />
      <CampaignsDrawer
        open={campaignsOpen}
        agentId={activeAgent?.id}
        agentName={activeAgent?.nickname || activeAgent?.name}
        agentImageUrl={activeAgent?.image_url}
        agentType={activeAgent?.agent_type as string}
        onClose={() => setCampaignsOpen(false)}
        toggleRef={campaignsToggleRef}
      />
    </nav>
  );
};

export default ExpansiveSideNavigation;
